import {useEffect} from "react";
import {useFormContext} from "react-hook-form";

import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {ServerErrors} from "@/lib/types/common.ts";
import {IntegrationFormData} from "@/lib/types/integrations.ts";
import {SetupInfo} from "@/pages/integrations/edit/subForms/components/SetupInfo.tsx";
import {IntegrationModuleInfo, IntegrationModules} from "@/pages/integrations/integrations.types.ts";

export type FormValuesKey = keyof IntegrationFormData;

interface WaveFrontFormProps {
  serverErrors?: ServerErrors;
}

export const WaveFrontForm = ({serverErrors}: WaveFrontFormProps) => {
  const {setError} = useFormContext();

  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors)
        .filter((k) => k !== "__all__")
        .forEach((key) => setError(`settings.${key}` as FormValuesKey, {message: serverErrors[key][0]}));
    }
  }, [setError, serverErrors]);

  return (
    <>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.wavefront_url"
            label="Root URL"
            required={true}
            tooltip="Root URL of your VMware Aria Operations for Applications service, eg. https://longboard.wavefront.com."
            autoFocus={true}
          />
        </div>
        <div className="col-6 mb-3">
          <InputField
            name="settings.csp_url"
            label="CSP Hostname URL"
            required={false}
            tooltip="Root URL of your VMware CSP service, for authentication, e.g. https://console.cloud.vmware.com. Leave blank to use a legacy API token."
          />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.csp_app_id"
            label="CSP OAuth App ID"
            required={false}
            tooltip="If you're using a Server to Server OAuth App for authentication, provide the OAuth App ID here. Otherwise, leave blank."
          />
        </div>
        <div className="col-6 mb-3">
          <InputField
            name="settings.api_token"
            label="API Token / OAuth App Secret"
            required={true}
            tooltip="If you're using a legacy API token or a CSP API Token, enter it here. If you're using a Server to Server OAuth App, enter your OAuth App Secret here."
          />
        </div>
      </div>
      <SetupInfo>
        <div className="p-2">
          <strong>VMware Aria Operations for Applications</strong>, previously known as Tanzu Observability powered by{" "}
          <strong>Wavefront</strong>.
        </div>
        <div className="p-2">
          <strong>Set Your Root URL</strong>
          <br />
          Log in to your VMware Aria Operations for Applications dashboard and copy the domain part of the URL into the
          &quot;Root URL&quot; field (this is the beginning of the URL, for example "https://longboard.wavefront.com").
        </div>
        <div className="p-2">
          <strong>Option 1: Authenticate with a CSP API Token</strong>
          <br />
          <a
            target="blank"
            href="https://docs.vmware.com/en/VMware-Cloud-services/services/Using-VMware-Cloud-Services/GUID-E2A3B1C1-E9AD-4B00-A6B6-88D31FCDDF7C.html"
          >
            Follow the official guide
          </a>{" "}
          for generating a CSP API Token. Enter your CSP Hostname URL in the corresponding field above, and leave the
          &quot;CSP OAuth App ID&quot; field blank. Then, enter the generated API token in the &quot;API Token / OAuth
          App Secret&quot; field.
        </div>
        <div className="p-2">
          <strong>Option 2: Authenticate with a CSP Server to Server OAuth App</strong>
          <br />
          <a
            target="blank"
            href="https://docs.wavefront.com/windows_host_tutorial.html#step-1-create-a-server-to-server-oauth-app-in-your-organization"
          >
            Follow the official guide
          </a>{" "}
          to create a Server to Server OAuth App in your CSP organization. Enter your CSP Hostname URL in the
          corresponding field above, copy the CSP App ID into the &quot;CSP OAuth App ID&quot; field, and copy your CSP
          App Secret into the &quot;API Token / OAuth App Secret&quot; field.
        </div>
        <div className="p-2">
          <strong>Option 3: Authenticate with a Legacy API Token</strong>
          <br />
          To use and retrieve a legacy API token, log in to your VMware Aria Operations for Applications dashboard and
          click the cog icon on the upper-right of the screen, followed by your email address in the drop-down menu, to
          access the User Profile screen. At the bottom you may generate an API token and copy it into the &quot;API
          Token / OAuth App Secret&quot; field above. You must leave the two CSP fields blank.
        </div>
        <div className="p-2">
          <a target="_blank" href="https://support.uptime.com/hc/en-us/articles/360002592080-Configuring-Wavefront">
            View {IntegrationModuleInfo[IntegrationModules.WAVEFRONT].label} Support Article &rarr;
          </a>
        </div>
      </SetupInfo>
    </>
  );
};
